import { Stack, TimePicker } from '@fluentui/react';
import { DatePicker } from '@h2oai/ui-kit';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { formatDefaultDateAndTime } from '../../../utils/utils';
import type { PageFilterProps } from '../types';

type DateRangeValue = {
  dateStart?: string;
  dateEnd?: string;
};

type DateRangeFilterProps = PageFilterProps<DateRangeValue> & {
  startLabel?: string;
  endLabel?: string;
  showTimePickers?: boolean;
};

const datePickerStyles = {
  root: {
    width: 170,
    '.ms-TextField-wrapper + span': { display: 'none' },
  },
};

const timePickerStyles = {
  root: { width: 120 },
};

const generateReadableValue = (startDate?: string, endDate?: string) => {
  if (!startDate && !endDate) return '';

  const start = formatDefaultDateAndTime(startDate);
  const end = formatDefaultDateAndTime(endDate);

  if (start && end) return `${start} to ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;

  return '';
};

export const DateRangeFilter = memo((props: DateRangeFilterProps) => {
  const { onChange, value, startLabel = 'Date start', endLabel = 'Date end', showTimePickers = false } = props;
  const safeValue = value ?? { dateStart: undefined, dateEnd: undefined };
  const hasInitialized = useRef(false);

  const dateStart = useMemo(
    () => (safeValue.dateStart ? new Date(safeValue.dateStart) : undefined),
    [safeValue.dateStart]
  );
  const dateEnd = useMemo(() => (safeValue.dateEnd ? new Date(safeValue.dateEnd) : undefined), [safeValue.dateEnd]);
  const today = useMemo(() => new Date(), []);
  const maxStartDate = useMemo(() => {
    if (dateEnd && dateEnd < today) return dateEnd;
    return today;
  }, [dateEnd, today]);

  useEffect(() => {
    if ((safeValue.dateStart || safeValue.dateEnd) && !hasInitialized.current) {
      const readableValue = generateReadableValue(safeValue.dateStart, safeValue.dateEnd);
      if (readableValue) onChange(safeValue, readableValue);
      hasInitialized.current = true;
    }
  }, [safeValue.dateStart, safeValue.dateEnd, onChange]);

  const handleDateStartChange = useCallback(
    (date: Date | null | undefined) => {
      const dateStart = date ? date.toISOString() : undefined;
      const newValue = { ...safeValue, dateStart };
      const readableValue = generateReadableValue(dateStart, safeValue.dateEnd);
      onChange(newValue, readableValue);
    },
    [onChange, safeValue.dateStart, safeValue.dateEnd]
  );

  const handleDateEndChange = useCallback(
    (date: Date | null | undefined) => {
      const dateEnd = date ? date.toISOString() : undefined;
      const newValue = { ...safeValue, dateEnd };
      const readableValue = generateReadableValue(safeValue.dateStart, dateEnd);
      onChange(newValue, readableValue);
    },
    [onChange, safeValue.dateStart, safeValue.dateEnd]
  );

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
      <DatePicker
        placeholder="Select Date"
        label={startLabel}
        styles={datePickerStyles}
        value={dateStart}
        onSelectDate={handleDateStartChange}
        maxDate={maxStartDate}
      />
      {showTimePickers && (
        <TimePicker
          allowFreeInput={false}
          allowFreeform={false}
          useHour12
          placeholder="Time"
          ariaLabel="Time picker start"
          styles={timePickerStyles}
          dateAnchor={dateStart}
          value={dateStart}
          onChange={(_e: unknown, date?: Date) => handleDateStartChange(date)}
        />
      )}
      <DatePicker
        placeholder="Select Date"
        label={endLabel}
        styles={datePickerStyles}
        value={dateEnd}
        onSelectDate={handleDateEndChange}
        minDate={dateStart}
        maxDate={today}
      />
      {showTimePickers && (
        <TimePicker
          allowFreeInput={false}
          allowFreeform={false}
          useHour12
          placeholder="Time"
          ariaLabel="Time picker end"
          styles={timePickerStyles}
          dateAnchor={dateEnd}
          value={dateEnd}
          onChange={(_e: unknown, date?: Date) => handleDateEndChange(date)}
        />
      )}
    </Stack>
  );
});
